import { useEffect, useState } from 'react';
import ModuleFilterExpandableSelect, { IrawSelectOption } from './ModuleFiltersExpandableSelect';
import { ImoduleFilter, ISortFilter } from '../../types/global';
import errorMessage from '../../utils/errorMessage';
import ModuleFiltersIcon from '../../assets/images/components/NudosComponents/ModuleFiltersIcon';
import { ICountryDetail } from '../../types/countries';
import { countriesList } from '../../services/countriesList';
import './ModuleFilters.scss';

export enum FilterTypesWithAlphabeticalSort {
  BUY_COUNTRY = 'BuyCountry',
  COUNTRY = 'Country'
}

interface ImoduleFiltersProps {
  moduleFilters: ImoduleFilter[];
  setModuleFilters: (moduleFilters: ImoduleFilter[]) => void;
  getModuleFiltersRequestFunction?: () => Promise<DTOgetModuleFiltersRequestFunction>;
  getFiltersList: TgetFiltersList;
  moduleFiltersTitlesMapping: { [key: string]: string };
  customGap?: number;
  countryCondition?: boolean;
  moduleFiltersIconContainerCustomClass?: string;
  optionTextCustomClass?: string;
  showSort?: boolean;
  onSortChange?: (sortFilter: ISortFilter) => void;
  showSortInFilter?: string;
  customHeightContainer?: string;
  singleSelectFilters?: { [key: string]: boolean };
  showReferenceLetters?: boolean;
  customSortClassName?: string;
  currentSortFilter?: ISortFilter;
}

/**
 * @property {ImoduleFilter[]} moduleFilters - An array of the current filters for the module. Corresponds to the state variable that stores the filters the user has selected.
 * @property {(moduleFilters: ImoduleFilter[]) => void} setModuleFilters - The function that updates the filters when the user selects/unselects a filter value. Corresponds to the state updater for the moduleFilters.
 * @property {() => Promise<DTOgetModuleFiltersRequestFunction>} getModuleFiltersRequestFunction -The request function that fetch the value of the filters for the module.
 * @property {TgetFiltersList} getFiltersList - A function that returns the array with the complete data and control functions for each of the filters. This function must accept as params the functions declared on the ModuleFilter component: formModuleFilters, handleClickFilterOption,  getFilterActiveStatus, which in turn must be used in the getFiltersList function to get the properties rawSelectOptions, handleChangeSelectedOption, isFilterActive for each filter. The title property of each filter will be shown on the filters tab. The openFunction and openStatus properties of each filter correspond to the state function and value that control if the filter is open or closed.
 * @property {{ [key: string]: string }} moduleFiltersTitlesMapping - An object with keys corresponding to the name under which each filter is returned in the getModuleFiltersRequestFunction, and the value is the name that is expected for that filter by the function that brings the filtered data.
 */
const ModuleFilters = ({
  moduleFilters,
  setModuleFilters,
  getModuleFiltersRequestFunction,
  getFiltersList,
  moduleFiltersTitlesMapping,
  customGap,
  countryCondition,
  moduleFiltersIconContainerCustomClass,
  optionTextCustomClass = '',
  showSort = false,
  onSortChange,
  showSortInFilter,
  customHeightContainer,
  singleSelectFilters,
  showReferenceLetters,
  customSortClassName = '',
  currentSortFilter
}: ImoduleFiltersProps) => {
  const [usersAvailableModuleFilters, setUsersAvailableModuleFilters] = useState<DTOgetModuleFiltersRequestFunction>();
  const [listOfCountriesToBuy, setListOfCountriesToBuy] = useState<ICountryDetail[]>();

  const requiresAlphabeticalSort = (filterTitle: string): boolean => {
    return Object.values(FilterTypesWithAlphabeticalSort).includes(filterTitle as FilterTypesWithAlphabeticalSort);
  };
  const getOptionActiveStyle = (filterName: string, optionValue?: string | number) => {
    const searchedFilter = moduleFilters.find((filter: ImoduleFilter) => filter.name === filterName);
    if (!searchedFilter) return false;
    const searchedOption = searchedFilter.values.find((value: string | number) => value === optionValue);
    if (!searchedOption) return false;
    if (searchedOption) return true;
  };
  const formModuleFilters = (
    filterTitle: string,
    defaultRawFilterOptions?: string[],
    nameToValueMap?: Map<string, string>
  ): IrawSelectOption[] => {
    if (
      !defaultRawFilterOptions &&
      (!usersAvailableModuleFilters || Object.entries(usersAvailableModuleFilters).length === 0)
    )
      return [];
    let rawFilterOptions;
    if (defaultRawFilterOptions) rawFilterOptions = [...defaultRawFilterOptions];
    if (!defaultRawFilterOptions && usersAvailableModuleFilters) {
      rawFilterOptions = usersAvailableModuleFilters[filterTitle];
    }
    if (!rawFilterOptions) return [];
    const filtersSelectOptions = rawFilterOptions.map(filterOptionName => {
      return {
        name: filterOptionName,
        value: nameToValueMap ? nameToValueMap.get(filterOptionName) : filterOptionName,
        isActive: getOptionActiveStyle(
          moduleFiltersTitlesMapping[filterTitle as keyof typeof moduleFiltersTitlesMapping],
          nameToValueMap ? nameToValueMap.get(filterOptionName) : filterOptionName
        ),
        id: filterOptionName,
        nameToSortAlphabetically: requiresAlphabeticalSort(filterTitle)
          ? listOfCountriesToBuy?.find(country => country.code === filterOptionName)?.name
          : undefined
      };
    });

    return filtersSelectOptions;
  };

  const handleResetFilters = () => setModuleFilters([]);

  const handleClickFilterOption =
    (filterName: string, filterOptionProperty: keyof IrawSelectOption) => (option: IrawSelectOption) => {
      const newFilters = [...moduleFilters];
      if (!option) return;
      const currentFilterIndex = newFilters.findIndex(filter => filter.name === filterName);
      const isSingleSelect = singleSelectFilters?.[filterName];

      if (currentFilterIndex >= 0) {
        const currentValueIndex = newFilters[currentFilterIndex]?.values.findIndex(
          (value: string | number) => value === option[filterOptionProperty]
        );

        if (currentValueIndex >= 0) {
          if (isSingleSelect) {
            newFilters.splice(currentFilterIndex, 1);
            if (countryCondition) {
              const noState = newFilters.find(element => element?.name !== 'status');
              if (noState) {
                const newList = newFilters.filter(element => element?.name !== 'country');
                setModuleFilters(newList);
              } else {
                setModuleFilters(newFilters);
              }
            } else {
              setModuleFilters(newFilters);
            }
            return;
          }

          if (newFilters[currentFilterIndex]?.values.length <= 1) {
            newFilters.splice(currentFilterIndex, 1);
            if (countryCondition) {
              const noState = newFilters.find(element => element?.name !== 'status');
              if (noState) {
                const newList = newFilters.filter(element => element?.name !== 'country');
                setModuleFilters(newList);
              } else {
                setModuleFilters(newFilters);
              }
            } else {
              setModuleFilters(newFilters);
            }
          } else {
            newFilters[currentFilterIndex]?.values.splice(currentValueIndex, 1);
            setModuleFilters(newFilters);
          }
        } else {
          if (isSingleSelect) {
            newFilters[currentFilterIndex].values = [option[filterOptionProperty]];
          } else {
            newFilters[currentFilterIndex]?.values.push(option[filterOptionProperty]);
          }
          setModuleFilters(newFilters);
        }
      } else {
        const newModuleFilter = {
          name: filterName,
          includes: 'in',
          values: [option[filterOptionProperty]]
        };
        newFilters.push(newModuleFilter);
        setModuleFilters(newFilters);
      }
    };

  const getFilterActiveStatus = (filterTitle: string) => moduleFilters.some(filter => filter.name === filterTitle);
  const filtersActiveStyle = moduleFilters.length > 0 ? 'active' : '';
  const filtersIcon = <ModuleFiltersIcon className="moduleFiltersIcon" role={filtersActiveStyle} />;

  const createFilterStateWrapper = (
    filterTitle: string,
    originalSetState: React.Dispatch<React.SetStateAction<boolean>>,
    allFilters: IModuleFilterInfo[]
  ) => {
    return (value: boolean | ((prev: boolean) => boolean)) => {
      const newValue = typeof value === 'function' ? value(false) : value;
      if (newValue) {
        allFilters.forEach(filter => {
          if (filter.title !== filterTitle) {
            filter.openFunction(false);
          }
        });
      }
      originalSetState(value);
    };
  };

  const filtersList: IModuleFilterInfo[] = getFiltersList(
    formModuleFilters,
    handleClickFilterOption,
    getFilterActiveStatus
  );

  const wrappedFiltersList: IModuleFilterInfo[] = filtersList.map(filter => ({
    ...filter,
    openFunction: createFilterStateWrapper(filter.title, filter.openFunction, filtersList)
  }));

  const formatCountryFilter = (details: ICountryDetail[]) => {
    const list: undefined | string[] = [];
    if (details && details.length > 0) {
      for (const country of details) {
        if (country?.code) {
          list.push(country.code);
        }
      }
      return list;
    }
    return [];
  };

  const getUsersModuleFilters = async () => {
    if (!getModuleFiltersRequestFunction) return;
    try {
      const userModuleFilters = await getModuleFiltersRequestFunction();
      const countryList = await countriesList('buy');
      setListOfCountriesToBuy(countryList);
      if (userModuleFilters)
        setUsersAvailableModuleFilters({
          ...userModuleFilters,
          Venta: ['Prendido', 'Apagado', 'Sin Stock'],
          CargueExternas: ['Prendido', 'Apagado'],
          BuyCountry: formatCountryFilter(countryList)
        });
    } catch {
      errorMessage();
    }
  };

  useEffect(() => {
    getUsersModuleFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="moduleFilters">
      <div
        className={`moduleFiltersIconContainer ${moduleFiltersIconContainerCustomClass} ${filtersActiveStyle}`}
        onClick={handleResetFilters}
      >
        {filtersIcon}
      </div>
      <div className="filtersContainer" style={{ gap: customGap ? `${customGap}px` : '' }}>
        {wrappedFiltersList.map((filter, i) => {
          const filterClass = filter?.filterClass || '';
          return (
            <ModuleFilterExpandableSelect
              key={`filter-${i}-${filter.title.replace(' ', '')}`}
              filterTitle={filter.title}
              openSelectStatus={filter.openStatus}
              openSelectFunction={filter.openFunction}
              rawSelectOptions={filter.rawSelectOptions}
              handleChangeSelectedOption={filter.handleChangeSelectedOption}
              isFilterActive={filter?.isFilterActive}
              conditionedFilter={filter?.conditionedFilter}
              filterUnlocked={filter?.filterUnlocked}
              aditionalInformation={filter?.aditionalInformation}
              countryListAlias={
                listOfCountriesToBuy && filter?.title === 'País' ? listOfCountriesToBuy || [] : undefined
              }
              optionTextCustomClass={filterClass || optionTextCustomClass}
              showSort={showSort}
              onSortChange={onSortChange}
              showSortInFilter={showSortInFilter}
              customHeightContainer={showSortInFilter === filter.title ? customHeightContainer : undefined}
              showSearch={filter?.showSearch}
              searchPlaceholder={filter?.searchPlaceholder}
              sortAlphabetically={filter?.sortAlphabetically}
              isLargeFilter={filter?.isLargeFilter}
              showReferenceLetters={showReferenceLetters}
              titleFilterContainer={filter?.titleFilterContainer}
              customSortClassName={customSortClassName}
              useRadioButtons={filter?.useRadioButtons ?? (singleSelectFilters?.[filter.title] || false)}
              currentSortFilter={currentSortFilter}
            />
          );
        })}
      </div>
    </section>
  );
};

export default ModuleFilters;

export interface IModuleFilterInfo {
  title: string;
  openFunction: React.Dispatch<React.SetStateAction<boolean>>;
  openStatus: boolean;
  rawSelectOptions: IrawSelectOption[];
  handleChangeSelectedOption: (option: IrawSelectOption) => void;
  isFilterActive: boolean;
  conditionedFilter?: boolean;
  filterUnlocked?: boolean;
  aditionalInformation?: string;
  filterClass?: string;
  showSearch?: boolean;
  searchPlaceholder?: string;
  sortAlphabetically?: boolean;
  isLargeFilter?: boolean;
  titleFilterContainer?: string;
  useRadioButtons?: boolean;
}

export interface DTOgetModuleFiltersRequestFunction {
  [key: string]: string[];
}

export type TgetFiltersList = (
  formModuleFilters: (
    filterTitle: string,
    defaultRawFilterOptions?: string[],
    nameToValueMap?: Map<string, string>
  ) => IrawSelectOption[],
  handleClickFilterOption: (
    filterName: string,
    filterOptionProperty: keyof IrawSelectOption
  ) => (option: IrawSelectOption) => void,
  getFilterActiveStatus: (filterTitle: string) => boolean
) => IModuleFilterInfo[];
